import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft, Loader2, MapPin,
  LayoutGrid, Landmark, Receipt,
} from 'lucide-react';
import { getCreditLineSubscriberDetail } from '../services/creditLineService';
import { useAuth } from '../auth/AuthContext';
import StatusBadge from '../components/common/StatusBadge';

// ── Role helper ───────────────────────────────────────────────────────────────

const getCurrentRole = () => {
  const authRole = localStorage.getItem('authRole');
  return { wallet_superadmin: 'SuperAdmin', wallet_ops: 'Ops' }[authRole] || null;
};

// ── Formatters ────────────────────────────────────────────────────────────────

const fmt = (v) => (v != null && v !== '') ? String(v) : null;

const fmtAmount = (v) => {
  const n = Number(v);
  if (isNaN(n) || v == null || v === '') return null;
  return `₹${n.toLocaleString('en-IN')}`;
};

const fmtDate = (s) => {
  if (!s) return null;
  try {
    const d = new Date(s);
    if (isNaN(d)) return null;
    return `${d.getDate()} ${d.toLocaleString('en-IN', { month: 'short' })} ${d.getFullYear()}`;
  } catch { return null; }
};

const fmtDateTime = (s) => {
  if (!s) return null;
  try {
    const d = new Date(s);
    if (isNaN(d)) return null;
    const h = d.getHours(), m = String(d.getMinutes()).padStart(2, '0');
    return `${d.getDate()} ${d.toLocaleString('en-IN', { month: 'short' })} ${d.getFullYear()}, ${h % 12 || 12}:${m} ${h >= 12 ? 'PM' : 'AM'}`;
  } catch { return null; }
};

const humanize = (s) => s ? s.replace(/[-_]/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : null;

const OWN_MAP = { owned: 'Owned', rented: 'Rented', family: 'Family', company: 'Company', o: 'Owned', r: 'Rented', f: 'Family' };
const EMP_MAP = { s: 'Salaried', e: 'Self Employed', p: 'Pensioner', u: 'Unemployed' };

// ── SectionCard — gradient header wash + colored hover shadow + tick mark ──────
// Ported from DiagnosticLabDetail.jsx's SectionCard (navy/teal palette).

const SectionCard = ({ title, subtitle, children, allowOverflow = false }) => (
  <div className={`bg-white rounded-2xl border border-[#E4EBF3] shadow-[0_1px_2px_rgba(15,76,129,0.04)] hover:shadow-[0_8px_24px_-10px_rgba(15,76,129,0.18)] transition-shadow duration-300 ${allowOverflow ? '' : 'overflow-hidden'}`}>
    <div className="px-6 py-4 border-b border-[#EEF3F8] bg-gradient-to-r from-[#F7FAFD] to-transparent">
      <h3 className="text-sm font-semibold text-[#0F4C81] flex items-center gap-2 before:content-[''] before:w-1 before:h-4 before:rounded-full before:bg-gradient-to-b before:from-[#0F4C81] before:to-[#00A6A6]">{title}</h3>
      {subtitle && <p className="text-xs text-gray-400 mt-0.5 pl-3">{subtitle}</p>}
    </div>
    <div className="px-6 py-5">{children}</div>
  </div>
);

// ── Tab bar with animated sliding pill ─────────────────────────────────────────
// Ported from DiagnosticLabDetail.jsx's LabTabBar.

const DETAIL_TABS = [
  { key: 'overview', label: 'Overview', icon: LayoutGrid },
  { key: 'lenders', label: 'Lenders', icon: Landmark },
  { key: 'ledger', label: 'Ledger', icon: Receipt },
];

const DetailTabBar = ({ active, onChange }) => (
  <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-1.5 flex gap-1 overflow-x-auto">
    {DETAIL_TABS.map((t) => {
      const Icon = t.icon;
      const sel = active === t.key;
      return (
        <button key={t.key} type="button" onClick={() => onChange(t.key)}
          className={`relative inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-colors ${sel ? 'text-[#0F4C81]' : 'text-gray-500 hover:text-gray-800 hover:bg-gray-50'}`}>
          {sel && (
            <motion.span layoutId="creditLineTabPill"
              className="absolute inset-0 rounded-xl bg-[#0F4C81]/[0.07] ring-1 ring-inset ring-[#0F4C81]/20"
              transition={{ type: 'spring', stiffness: 400, damping: 32 }} />
          )}
          <Icon className="w-4 h-4 relative z-10" />
          <span className="relative z-10">{t.label}</span>
        </button>
      );
    })}
  </div>
);

const panelMotion = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  transition: { duration: 0.2, ease: 'easeOut' },
};

// ── KV primitive ──────────────────────────────────────────────────────────────

const KV = ({ label, value, mono = false, highlight = false }) => (
  <div className="flex flex-col gap-0.5 min-w-0">
    <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest truncate">{label}</span>
    <span className={[
      'text-sm leading-snug break-words',
      highlight ? 'text-emerald-600 font-bold' : 'text-gray-800',
      mono ? 'font-mono text-xs' : '',
      !value ? 'italic text-gray-300' : '',
    ].filter(Boolean).join(' ')}>
      {value || '—'}
    </span>
  </div>
);

// ── Empty state ───────────────────────────────────────────────────────────────

const Empty = ({ label = 'No information available' }) => (
  <p className="text-xs text-gray-400 italic py-1">{label}</p>
);

// ── Address block ─────────────────────────────────────────────────────────────

const ADDR_ACCENT = {
  resident_address: { dot: 'bg-cyan-400', badge: 'bg-cyan-50 text-cyan-700', label: 'Resident' },
  permanent_address: { dot: 'bg-violet-400', badge: 'bg-violet-50 text-violet-700', label: 'Permanent' },
  company_address: { dot: 'bg-orange-400', badge: 'bg-orange-50 text-orange-700', label: 'Company' },
};

const AddressBlock = ({ type, addr }) => {
  if (!addr) return null;
  const acc = ADDR_ACCENT[type] || ADDR_ACCENT.resident_address;
  const line = [addr.address_line_1, addr.address_line_2].filter(Boolean).join(', ');
  return (
    <div className="rounded-xl border border-[#E4EBF3] bg-[#FAFCFE] p-3 space-y-1.5">
      <div className="flex items-center gap-1.5">
        <span className={`w-1.5 h-1.5 rounded-full ${acc.dot}`} />
        <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${acc.badge}`}>{acc.label}</span>
        {addr.ownership_type && (
          <span className="ml-auto text-[10px] text-gray-400">{OWN_MAP[addr.ownership_type] || humanize(addr.ownership_type)}</span>
        )}
      </div>
      {line ? (
        <div className="flex gap-1.5">
          <MapPin className="w-3 h-3 text-gray-400 mt-0.5 shrink-0" />
          <p className="text-xs text-gray-600 leading-snug">{line}</p>
        </div>
      ) : null}
      {addr.landmark ? <p className="text-[10px] text-gray-400 pl-4">{addr.landmark}</p> : null}
      <div className="flex flex-wrap gap-2 text-[10px] text-gray-500">
        {addr.city && <span>{addr.city}</span>}
        {addr.state && <span>· {addr.state}</span>}
        {addr.pincode && <span className="font-mono bg-white border border-gray-200 px-1 rounded">{addr.pincode}</span>}
      </div>
    </div>
  );
};

// ── Repayment row ─────────────────────────────────────────────────────────────

const RepaymentRow = ({ repayment }) => (
  <div className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg bg-white border border-[#E4EBF3]">
    <div className="min-w-0">
      <p className="text-xs font-semibold text-gray-800">{fmtAmount(repayment.amount) || '—'}</p>
      <p className="text-[10px] text-gray-400">
        {humanize(repayment.payment_mode) || 'Mode —'} · Due {fmtDate(repayment.due_date) || '—'}
        {repayment.paid_at && ` · Paid ${fmtDateTime(repayment.paid_at)}`}
      </p>
    </div>
    <StatusBadge status={repayment.status} label={repayment.status_display} />
  </div>
);

// ── Ledger card ───────────────────────────────────────────────────────────────

const LedgerCard = ({ ledger }) => {
  const repayments = ledger.repayments || [];
  return (
    <div className="rounded-xl bg-[#FAFCFE] border border-[#E4EBF3] px-4 py-3 space-y-3">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="text-sm font-semibold text-gray-900">{fmtAmount(ledger.amount) || '—'}</p>
          <p className="text-[10px] text-gray-400">
            {humanize(ledger.txn_type)} · Created {fmtDate(ledger.created_at) || '—'}
            {ledger.repay_due_date && ` · Due ${fmtDate(ledger.repay_due_date)}`}
          </p>
        </div>
        <StatusBadge status={ledger.status} label={ledger.status_display} />
      </div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-2">
        <KV label="Amount Paid" value={fmtAmount(ledger.amount_paid)} highlight />
        <KV label="Outstanding" value={fmtAmount(ledger.outstanding_balance)} />
      </div>
      {repayments.length > 0 && (
        <>
          <div className="border-t border-[#E4EBF3]" />
          <div className="space-y-1.5">
            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest">Repayments ({repayments.length})</p>
            {repayments.map((r) => <RepaymentRow key={r.id} repayment={r} />)}
          </div>
        </>
      )}
    </div>
  );
};

// ── Lender card ───────────────────────────────────────────────────────────────

const LenderCard = ({ lender, showLedgers = true }) => {
  const ledgers = lender.ledgers || [];
  const initial = (lender.credit_lender?.name || 'L').charAt(0).toUpperCase();
  return (
    <div className="rounded-2xl bg-[#F7FAFD] border border-[#E4EBF3] px-4 py-4 space-y-3">
      <div className="flex items-center gap-2.5 flex-wrap">
        <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#0F4C81] to-[#00A6A6] text-white flex items-center justify-center font-semibold text-sm shrink-0 shadow-sm">
          {initial}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-gray-900 truncate">{lender.credit_lender?.name || '—'}</p>
          {lender.credit_lender?.code && <p className="text-[10px] text-[#0F4C81]/70 font-medium">{lender.credit_lender.code}</p>}
        </div>
        <StatusBadge status={lender.status} label={lender.status_display} />
      </div>
      <div className="border-t border-[#E4EBF3]" />
      <div className="grid grid-cols-2 gap-x-4 gap-y-3">
        <KV label="Credit Limit" value={fmtAmount(lender.credit_limit)} highlight />
        <KV label="Available Limit" value={fmtAmount(lender.available_limit)} />
        {lender.pre_approval_expire_at && <KV label="Pre-approval Expires" value={fmtDate(lender.pre_approval_expire_at)} />}
      </div>
      {showLedgers && (
        <>
          <div className="border-t border-[#E4EBF3]" />
          <div className="space-y-2">
            <p className="text-[10px] font-semibold text-[#0F4C81]/60 uppercase tracking-widest">Ledger History ({ledgers.length})</p>
            {ledgers.length === 0 ? (
              <Empty label="No ledger entries yet" />
            ) : (
              ledgers.map((ledger) => <LedgerCard key={ledger.id} ledger={ledger} />)
            )}
          </div>
        </>
      )}
    </div>
  );
};

// ── Main page ─────────────────────────────────────────────────────────────────

const CreditLineSubscriberDetail = () => {
  const { application_id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const currentRole = getCurrentRole();

  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('overview');

  const fetchDetail = useCallback(async () => {
    if (authLoading || !isAuthenticated || !currentRole || !application_id) return;
    setLoading(true);
    setError('');
    try {
      const res = await getCreditLineSubscriberDetail(currentRole, application_id);
      if (!res?.metadata?.api_status) throw new Error(typeof res?.msg === 'string' ? res.msg : 'API returned an error status');
      setDetail(res.msg);
    } catch (err) {
      setError(err.message || 'Failed to load credit line subscriber detail');
    } finally {
      setLoading(false);
    }
  }, [authLoading, isAuthenticated, currentRole, application_id]);

  useEffect(() => { fetchDetail(); }, [fetchDetail]);

  if (!currentRole) {
    return (
      <div className="flex justify-center items-center h-64 text-center">
        <div>
          <h2 className="text-2xl font-bold text-red-600 mb-2">Access Denied</h2>
          <p className="text-gray-600">You do not have permission to access this page.</p>
        </div>
      </div>
    );
  }

  const member = detail?.member;
  const emp = detail?.employment_details;
  const lenders = detail?.lenders || [];
  const totalLedgers = lenders.reduce((sum, l) => sum + (l.ledgers?.length || 0), 0);
  const totalCreditLimit = lenders.reduce((sum, l) => sum + (Number(l.credit_limit) || 0), 0);
  const totalAvailableLimit = lenders.reduce((sum, l) => sum + (Number(l.available_limit) || 0), 0);

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F5F9FD] to-[#E9F1F8] p-4 sm:p-8 flex justify-center">
      <div className="w-full max-w-5xl space-y-6">

        {/* Header */}
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={() => navigate('/subscriber-wallet/credit-line-subscribers')}
              className="p-2 text-gray-500 hover:text-gray-900 hover:bg-white rounded-lg border border-transparent hover:border-gray-200 transition-all shrink-0"
              aria-label="Back"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h1 className="text-xl font-bold text-gray-900 truncate">{loading ? 'Loading…' : (member?.name || 'Subscriber')}</h1>
                {detail && <StatusBadge status={detail.status} label={detail.status_display} />}
              </div>
              <p className="text-sm text-gray-400">Credit line account details</p>
            </div>
          </div>
        </div>

        {error && <div className="px-4 py-3 rounded-xl bg-red-50 border border-red-100 text-sm text-red-700 flex items-center justify-between">
          <span>{error}</span>
          <button onClick={fetchDetail} className="text-xs underline ml-4 hover:text-red-900">Retry</button>
        </div>}

        {loading || !detail ? (
          !error && (
            <div className="bg-white rounded-2xl border border-gray-200 p-16 flex flex-col items-center gap-3 text-gray-400">
              <Loader2 className="w-6 h-6 animate-spin" /><span className="text-sm">Loading subscriber…</span>
            </div>
          )
        ) : (
          <>
            <DetailTabBar active={activeTab} onChange={setActiveTab} />

            {activeTab === 'overview' && (
              <motion.div key="overview" {...panelMotion} className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-6">
                  <SectionCard title="Credit Limit">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="rounded-2xl bg-gradient-to-br from-[#0F4C81] to-[#0C3E6B] px-4 py-3.5 relative overflow-hidden">
                        <div className="absolute -top-3 -right-3 w-14 h-14 rounded-full bg-white/5 pointer-events-none" />
                        <p className="relative text-[10px] font-semibold text-white/70 uppercase tracking-widest">Total Credit Limit</p>
                        <p className="relative text-xl font-bold text-white mt-1 leading-none">{fmtAmount(totalCreditLimit) || '—'}</p>
                      </div>
                      <div className="rounded-2xl bg-gradient-to-br from-[#00A6A6] to-[#0F8C8C] px-4 py-3.5 relative overflow-hidden">
                        <div className="absolute -top-3 -right-3 w-14 h-14 rounded-full bg-white/5 pointer-events-none" />
                        <p className="relative text-[10px] font-semibold text-white/70 uppercase tracking-widest">Total Available Limit</p>
                        <p className="relative text-xl font-bold text-white mt-1 leading-none">{fmtAmount(totalAvailableLimit) || '—'}</p>
                      </div>
                    </div>
                  </SectionCard>

                  {member && (
                    <SectionCard title="Subscriber">
                      <div className="rounded-xl bg-[#FAFCFE] border border-[#E4EBF3] px-4 py-3 space-y-3">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-gray-900 truncate">{fmt(member.name) || '—'}</p>
                            {fmt(member.contact_number) && <p className="text-[10px] text-[#0F4C81] font-medium mt-0.5">{member.contact_number}</p>}
                            {fmt(member.email) && <p className="text-[10px] text-gray-400 truncate">{member.email}</p>}
                          </div>
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold bg-[#0F4C81] text-white uppercase shrink-0">
                            <span className="w-1.5 h-1.5 rounded-full bg-[#00A6A6] inline-block" />Borrower
                          </span>
                        </div>
                        <div className="border-t border-[#E4EBF3]" />
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-3">
                          <KV label="Gender" value={humanize(member.gender)} />
                          <KV label="Date of Birth" value={fmtDate(member.dob)} />
                          <KV label="Relation" value={humanize(member.relation)} />
                          <KV label="PAN" value={fmt(member.pan_number)} mono />
                          <div className="col-span-2 lg:col-span-2">
                            <KV label="Aadhar" value={fmt(member.aadhar_number)} mono />
                          </div>
                        </div>
                      </div>
                    </SectionCard>
                  )}

                  <SectionCard title="Account Summary">
                    <div className="grid grid-cols-2 gap-2">
                      <div className="bg-[#F7FAFD] rounded-xl px-3 py-2.5 border border-[#E4EBF3]">
                        <p className="text-[10px] text-[#0F4C81]/60 uppercase tracking-widest">Lenders</p>
                        <p className="text-lg font-bold text-gray-900 mt-0.5">{lenders.length}</p>
                      </div>
                      <div className="bg-[#F7FAFD] rounded-xl px-3 py-2.5 border border-[#E4EBF3]">
                        <p className="text-[10px] text-[#0F4C81]/60 uppercase tracking-widest">Ledger Entries</p>
                        <p className="text-lg font-bold text-gray-900 mt-0.5">{totalLedgers}</p>
                      </div>
                    </div>
                  </SectionCard>
                </div>

                <div className="space-y-6">
                  {emp && (
                    <SectionCard title="Employment">
                      <div className="rounded-xl bg-[#FAFCFE] border border-[#E4EBF3] px-3 py-3 space-y-3">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <p className="text-xs font-semibold text-gray-900 truncate">{fmt(emp.company_name) || '—'}</p>
                            <p className="text-[10px] text-[#0F4C81] font-medium mt-0.5">
                              {EMP_MAP[emp.employment_type] || humanize(emp.employment_type) || '—'}
                            </p>
                          </div>
                          {emp.monthly_income != null && (
                            <div className="text-right shrink-0">
                              <p className="text-[10px] text-gray-400 uppercase tracking-wider">Income</p>
                              <p className="text-sm font-bold text-emerald-600">{fmtAmount(emp.monthly_income)}</p>
                            </div>
                          )}
                        </div>
                        <div className="border-t border-[#E4EBF3]" />
                        <div className="grid grid-cols-2 gap-x-3 gap-y-3">
                          <KV label="Experience" value={emp.work_experience != null ? `${emp.work_experience} yrs` : null} />
                          <KV label="Salary via" value={humanize(emp.medium_of_salary)} />
                        </div>
                      </div>
                    </SectionCard>
                  )}

                  {(detail.resident_address || detail.permanent_address || detail.company_address) && (
                    <SectionCard title="Addresses">
                      <div className="space-y-2">
                        <AddressBlock type="resident_address" addr={detail.resident_address} />
                        <AddressBlock type="permanent_address" addr={detail.permanent_address} />
                        <AddressBlock type="company_address" addr={detail.company_address} />
                      </div>
                    </SectionCard>
                  )}
                </div>
              </motion.div>
            )}

            {activeTab === 'lenders' && (
              <motion.div key="lenders" {...panelMotion}>
                <SectionCard title="Lenders" subtitle={`${lenders.length} lender${lenders.length !== 1 ? 's' : ''} attached to this account`}>
                  {lenders.length === 0 ? (
                    <Empty label="No lenders attached to this credit line account" />
                  ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                      {lenders.map((lender) => <LenderCard key={lender.id} lender={lender} showLedgers={false} />)}
                    </div>
                  )}
                </SectionCard>
              </motion.div>
            )}

            {activeTab === 'ledger' && (
              <motion.div key="ledger" {...panelMotion}>
                <SectionCard title="Ledger & Repayments" subtitle={`${totalLedgers} ledger entr${totalLedgers !== 1 ? 'ies' : 'y'} across all lenders`}>
                  {lenders.length === 0 ? (
                    <Empty label="No lenders attached to this credit line account" />
                  ) : (
                    <div className="space-y-4">
                      {lenders.map((lender) => <LenderCard key={lender.id} lender={lender} showLedgers />)}
                    </div>
                  )}
                </SectionCard>
              </motion.div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default CreditLineSubscriberDetail;
